import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from 'recoil';
import { v4 as uuidv4 } from 'uuid';
import { ToastType } from '@/components/Toast/Toast';
import { MAX_FILE_SIZE, NUMBER_OF_ACCEPTED_FILES, ONE_MB, } from '@/config/constants';
import { useSocket } from '@/hooks/useSocket';
import { addToastSelector } from '@/state/config';
import { filesToUploadLengthSelector, filesToUploadState, isUploadingFileState, } from '@/state/session';
import { uploadFile } from '../components/MessageInput/FilesCarousel/utils';
export const useFileUploadHandlers = () => {
    const { t } = useTranslation();
    const filesToUpload = useRecoilValue(filesToUploadState);
    const setFilesToUpload = useSetRecoilState(filesToUploadState);
    const setIsUploadingFile = useSetRecoilState(isUploadingFileState);
    const filesToUploadLength = useRecoilValue(filesToUploadLengthSelector);
    const addToast = useRecoilCallback(({ snapshot, set }) => (toast) => {
        const loadable = snapshot.getLoadable(addToastSelector(toast));
        if (loadable.state === 'hasValue') {
            set(addToastSelector(toast), loadable.contents);
        }
    });
    const { requestUploadPresignedUrl } = useSocket();
    const handleFileUpload = useCallback(async (file, upload_url) => {
        setIsUploadingFile(true);
        const response = await uploadFile(upload_url, file === null || file === void 0 ? void 0 : file.original_file);
        const resp = JSON.parse(response);
        if ((resp === null || resp === void 0 ? void 0 : resp.message) === 'success') {
            if (file.preview_url) {
                URL.revokeObjectURL(file.preview_url);
            }
        }
        else if ((resp === null || resp === void 0 ? void 0 : resp.message) === 'error') {
            addToast({
                type: ToastType.warning,
                message: `File upload failed. Try again.`,
            });
            const fileIndex = filesToUpload.findIndex((f) => f.file_id === file.file_id);
            if (fileIndex !== -1) {
                setFilesToUpload((prev) => {
                    const updatedFiles = [...prev];
                    updatedFiles[fileIndex].upload_error = true;
                    return updatedFiles;
                });
            }
        }
        setIsUploadingFile(false);
    }, [addToast, filesToUpload, setFilesToUpload, setIsUploadingFile]);
    const handleExcessFiles = useCallback((validFiles) => {
        if (validFiles.length > NUMBER_OF_ACCEPTED_FILES) {
            // remove last n excess files
            validFiles.splice(NUMBER_OF_ACCEPTED_FILES - filesToUploadLength, validFiles.length);
            addToast({
                type: ToastType.attention,
                message: `File limit of ${NUMBER_OF_ACCEPTED_FILES} exceeded. Extra files omitted.`,
            });
        }
    }, [addToast, filesToUploadLength]);
    const handleSelectedFiles = useCallback((file) => {
        const data = {
            fileId: uuidv4(),
            fileType: file.name.split('.').pop(),
            originalName: file.name,
        };
        setFilesToUpload((prev) => [
            ...prev,
            {
                original_name: file.name,
                file_id: data.fileId,
                content_type: file.type,
                preview_url: URL.createObjectURL(file),
                upload_error: false,
                original_file: file,
                file_type: file.name.split('.').pop(),
            },
        ]);
        requestUploadPresignedUrl(data);
    }, [requestUploadPresignedUrl, setFilesToUpload]);
    const handleFileRejection = useCallback(({ file, errors }) => {
        errors.forEach((error) => {
            var _a;
            if (error.code === 'file-too-large') {
                addToast({
                    type: ToastType.warning,
                    message: t('files.too_large', {
                        fileName: file.name,
                        maxSize: MAX_FILE_SIZE / ONE_MB,
                    }),
                });
            }
            else if (error.code === 'file-invalid-type') {
                const extension = (_a = file.name.split('.').pop()) === null || _a === void 0 ? void 0 : _a.toLowerCase();
                let message = t('files.invalid_type', { fileName: file.name });
                if (extension === 'jfif') {
                    message = `${message} JFIF files are not supported.`;
                }
                addToast({
                    type: ToastType.warning,
                    message,
                });
            }
        });
    }, [addToast, t]);
    const onDrop = useCallback((acceptedFiles, fileRejections) => {
        // Filter out JFIF files from acceptedFiles
        const filteredFiles = acceptedFiles.filter((file) => {
            var _a;
            const extension = (_a = file.name.split('.').pop()) === null || _a === void 0 ? void 0 : _a.toLowerCase();
            if (extension === 'jfif') {
                // Add JFIF to rejections with custom error
                const customRejection = {
                    file,
                    errors: [
                        {
                            code: 'file-invalid-type',
                            message: `File ${file.name} has unsupported extension: .jfif`,
                        },
                    ],
                };
                fileRejections.push(customRejection);
                return false;
            }
            return true;
        });
        // Handle accepted files (using filtered list)
        handleExcessFiles(filteredFiles);
        filteredFiles.forEach(handleSelectedFiles);
        // Handle rejected files
        if (fileRejections.length > 0) {
            fileRejections.forEach(handleFileRejection);
        }
    }, [handleExcessFiles, handleSelectedFiles, handleFileRejection]);
    return {
        handleFileUpload,
        handleExcessFiles,
        handleSelectedFiles,
        onDrop,
    };
};
